import React, { useEffect, useState } from "react";
import Layout from "../../Component/Layout";
import axios from "axios";
import { hasPermission } from "../../utils/permission";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MASTER_API } from "../../apiConfig";

// ── Terms & Conditions Configuration ──────────────────────────────────────
// One screen, two templates, both stored as JSON in master_document_templates
// (see Templates.service.js on the backend):
//   template_key = 'quotation' -> content.rows      -> QuotationTermsPanel
//   template_key = 'po'        -> content.sections   -> PoTermsPanel
// A tab switches which panel is visible; both keep their own state so
// switching tabs doesn't lose unsaved edits in the other one.
// ───────────────────────────────────────────────────────────────────────────

const showToast = (message, type = "error") => {
  if (type === "success") toast.success(message);
  else toast.error(message);
};

/* ============================== QUOTATION ============================== */

// Rows the quotation computes itself from live RFQ data and never stores a
// fixed template value for — shown here as read-only info rows.
const COMPUTED_TERM_NOS = [6, 10];
const COMPUTED_LABELS = {
  6: "Pulled automatically from the Payment Terms selected on the quotation.",
  10: "Pulled automatically from the quotation's Validity Date.",
};

// Same font stack the quotation PDF uses for this table, so this page is a
// true preview of what will render. Swap these for your actual CAL / ABADI
// font-family values if QuotationView.js defines them differently.
const HEADING_FONT = "'Calibri', sans-serif";
const BODY_FONT = "'Cambria', 'Georgia', serif";

const baseCellStyle = {
  border: "1px solid black",
  padding: "8px 10px",
  verticalAlign: "top",
};

function QuotationTermsPanel({ canEdit }) {
  const [terms, setTerms] = useState([]); // [{ term_no, title, text, computed }]
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTerms = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${MASTER_API}/configuration/templates/quotation`);
      const rows = res.data?.content?.rows;
      setTerms(Array.isArray(rows) ? [...rows].sort((a, b) => a.term_no - b.term_no) : []);
    } catch (err) {
      if (err?.response?.status === 404) {
        setTerms([]);
      } else {
        console.error("loadTerms Error:", err);
        showToast("Could not load Terms & Conditions. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const updateField = (term_no, field, value) => {
    setTerms((prev) => prev.map((t) => (t.term_no === term_no ? { ...t, [field]: value } : t)));
  };

  const addRow = () => {
    if (!canEdit) {
      showToast("You don't have Edit access. Please contact admin.", "error");
      return;
    }
    const nextNo = terms.length > 0 ? Math.max(...terms.map((t) => t.term_no)) + 1 : 1;
    setTerms((prev) => [...prev, { term_no: nextNo, title: "", text: "", computed: false }]);
  };

  const removeRow = (term_no) => {
    if (!canEdit) {
      showToast("You don't have Edit access. Please contact admin.", "error");
      return;
    }
    if (COMPUTED_TERM_NOS.includes(term_no)) return; // can't remove computed rows
    setTerms((prev) => prev.filter((t) => t.term_no !== term_no));
  };

  const handleSave = async () => {
    if (!canEdit) {
      showToast("You don't have Edit access. Please contact admin.", "error");
      return;
    }
    try {
      setSaving(true);
      const res = await axios.put(`${MASTER_API}/configuration/templates/quotation`, {
        content: { rows: terms },
      });
      const rows = res.data?.content?.rows;
      setTerms(Array.isArray(rows) ? rows : terms);
      showToast(
        "Saved. This template now applies to every quotation that hasn't been individually edited.",
        "success"
      );
    } catch (err) {
      console.error("saveTerms Error:", err);
      showToast("Failed to save Terms & Conditions. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <p className="text-muted mb-0" style={{ maxWidth: "800px" }}>
          Master template for the "General Terms & Conditions" table shown on every quotation PDF
          — edit it exactly as it will appear. Saving updates <strong>all</strong> quotations that
          haven't had their own Terms & Conditions individually edited on the quotation page.
        </p>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={addRow} disabled={loading}>
            + Add Row
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={loading || saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : (
        <>
          {/* ── This block mirrors QuotationView.js's actual rendered table:
                 same heading, same underline, same borders/column widths.
                 Editing here IS editing the template — what you see is
                 what prints. ── */}
          <p
            style={{
              fontFamily: HEADING_FONT,
              fontSize: "20px",
              fontWeight: "normal",
              textDecoration: "underline",
              margin: "12px 0 8px 0",
              color: "#000",
            }}
          >
            General Terms &amp; Conditions
          </p>

          <table
            cellPadding="0"
            cellSpacing="0"
            width="100%"
            style={{ borderCollapse: "collapse", border: "1px solid black", tableLayout: "fixed" }}
          >
            <colgroup>
              <col style={{ width: "5%" }} />
              <col style={{ width: "20%" }} />
              <col style={{ width: "68%" }} />
              <col style={{ width: "7%" }} />
            </colgroup>
            <thead>
              <tr>
                <th style={{ ...baseCellStyle, fontFamily: HEADING_FONT, textAlign: "center" }}>#</th>
                <th style={{ ...baseCellStyle, fontFamily: HEADING_FONT }}>Title</th>
                <th style={{ ...baseCellStyle, fontFamily: HEADING_FONT }}>Text</th>
                <th style={{ ...baseCellStyle, border: "none" }}></th>
              </tr>
            </thead>
            <tbody>
              {terms.map((row, i) => {
                const isComputed = COMPUTED_TERM_NOS.includes(row.term_no);
                return (
                  <tr key={row.term_no}>
                    <td style={{ ...baseCellStyle, fontFamily: BODY_FONT, textAlign: "center" }}>{i + 1}.</td>
                    <td style={{ ...baseCellStyle, fontFamily: BODY_FONT }}>
                      <input
                        type="text"
                        value={row.title}
                        onChange={(e) => updateField(row.term_no, "title", e.target.value)}
                        disabled={isComputed || !canEdit}
                        placeholder="TITLE"
                        style={{
                          width: "100%",
                          fontFamily: BODY_FONT,
                          fontWeight: "bold",
                          fontSize: "14px",
                          color: "#000",
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          padding: 0,
                        }}
                      />
                    </td>
                    <td style={{ ...baseCellStyle, fontFamily: BODY_FONT }}>
                      {isComputed ? (
                        <span style={{ fontStyle: "italic", color: "#666" }}>{COMPUTED_LABELS[row.term_no]}</span>
                      ) : (
                        <textarea
                          value={row.text || ""}
                          onChange={(e) => updateField(row.term_no, "text", e.target.value)}
                          disabled={!canEdit}
                          rows={Math.max(2, Math.ceil((row.text || "").length / 90))}
                          style={{
                            width: "100%",
                            fontFamily: BODY_FONT,
                            fontSize: "14px",
                            color: "#000",
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            resize: "vertical",
                            padding: 0,
                          }}
                        />
                      )}
                    </td>
                    <td style={{ ...baseCellStyle, textAlign: "center" }}>
                      {!isComputed && canEdit && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => removeRow(row.term_no)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}

/* ================================== PO =================================== */

// Two section "types", matching what the PO document actually contains:
//   - "list":    a heading + a flat numbered list of clauses (e.g. "NOTE")
//   - "clauses": a heading + intro paragraph + numbered sub-clauses, each
//                with its own heading and one or more paragraphs
//                (e.g. "General Terms & Conditions of Purchase Order...")
const uid = () => Math.random().toString(36).slice(2, 9);

const emptyListSection = () => ({ id: uid(), heading: "", type: "list", items: [""] });

const emptyClausesSection = () => ({
  id: uid(),
  heading: "",
  type: "clauses",
  intro: "",
  clauses: [{ number: "1", heading: "", paragraphs: [""] }],
});

const panelStyle = {
  border: "1px solid #dee2e6",
  borderRadius: "6px",
  padding: "16px",
  marginBottom: "16px",
  background: "#fff",
};

function PoTermsPanel({ canEdit }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadTemplate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTemplate = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${MASTER_API}/configuration/templates/po`);
      const secs = res.data?.content?.sections;
      const withIds = (Array.isArray(secs) ? secs : []).map((s) => ({ ...s, id: s.id || uid() }));
      setSections(withIds);
    } catch (err) {
      if (err?.response?.status === 404) {
        setSections([]);
      } else {
        console.error("loadTemplate Error:", err);
        showToast("Could not load the PO Terms & Conditions template. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const requireEdit = () => {
    if (!canEdit) {
      showToast("You don't have Edit access. Please contact admin.", "error");
      return false;
    }
    return true;
  };

  const addSection = (type) => {
    if (!requireEdit()) return;
    setSections((prev) => [...prev, type === "list" ? emptyListSection() : emptyClausesSection()]);
  };

  const removeSection = (id) => {
    if (!requireEdit()) return;
    setSections((prev) => prev.filter((s) => s.id !== id));
  };

  const updateSectionHeading = (id, heading) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, heading } : s)));
  };

  const updateSectionIntro = (id, intro) => {
    setSections((prev) => prev.map((s) => (s.id === id ? { ...s, intro } : s)));
  };

  const addItem = (sectionId) => {
    if (!requireEdit()) return;
    setSections((prev) => prev.map((s) => (s.id === sectionId ? { ...s, items: [...s.items, ""] } : s)));
  };

  const updateItem = (sectionId, idx, value) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId ? { ...s, items: s.items.map((it, i) => (i === idx ? value : it)) } : s
      )
    );
  };

  const removeItem = (sectionId, idx) => {
    if (!requireEdit()) return;
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, items: s.items.filter((_, i) => i !== idx) } : s))
    );
  };

  const addClause = (sectionId) => {
    if (!requireEdit()) return;
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? { ...s, clauses: [...s.clauses, { number: String(s.clauses.length + 1), heading: "", paragraphs: [""] }] }
          : s
      )
    );
  };

  const removeClause = (sectionId, cIdx) => {
    if (!requireEdit()) return;
    setSections((prev) =>
      prev.map((s) => (s.id === sectionId ? { ...s, clauses: s.clauses.filter((_, i) => i !== cIdx) } : s))
    );
  };

  const updateClauseField = (sectionId, cIdx, field, value) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? { ...s, clauses: s.clauses.map((c, i) => (i === cIdx ? { ...c, [field]: value } : c)) }
          : s
      )
    );
  };

  const addParagraph = (sectionId, cIdx) => {
    if (!requireEdit()) return;
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? { ...s, clauses: s.clauses.map((c, i) => (i === cIdx ? { ...c, paragraphs: [...c.paragraphs, ""] } : c)) }
          : s
      )
    );
  };

  const updateParagraph = (sectionId, cIdx, pIdx, value) => {
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              clauses: s.clauses.map((c, i) =>
                i === cIdx ? { ...c, paragraphs: c.paragraphs.map((p, j) => (j === pIdx ? value : p)) } : c
              ),
            }
          : s
      )
    );
  };

  const removeParagraph = (sectionId, cIdx, pIdx) => {
    if (!requireEdit()) return;
    setSections((prev) =>
      prev.map((s) =>
        s.id === sectionId
          ? {
              ...s,
              clauses: s.clauses.map((c, i) =>
                i === cIdx ? { ...c, paragraphs: c.paragraphs.filter((_, j) => j !== pIdx) } : c
              ),
            }
          : s
      )
    );
  };

  const handleSave = async () => {
    if (!requireEdit()) return;
    try {
      setSaving(true);
      const res = await axios.put(`${MASTER_API}/configuration/templates/po`, { content: { sections } });
      const secs = res.data?.content?.sections;
      setSections(Array.isArray(secs) ? secs.map((s) => ({ ...s, id: s.id || uid() })) : sections);
      showToast("Saved. This is the template shown in every PO's Terms and Conditions modal.", "success");
    } catch (err) {
      console.error("saveTemplate Error:", err);
      showToast("Failed to save the PO Terms & Conditions template. Please try again.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <p className="text-muted mb-0" style={{ maxWidth: "800px" }}>
          Master template for the "Terms and Conditions" document shown on every Purchase Order
          (View More). Use <strong>List Section</strong> for a short numbered list (like "NOTE"),
          and <strong>Clauses Section</strong> for a long document with its own sub-headings.
        </p>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary" onClick={() => addSection("list")} disabled={loading}>
            + Add List Section
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => addSection("clauses")} disabled={loading}>
            + Add Clauses Section
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={loading || saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      {loading ? (
        <p>Loading…</p>
      ) : (
        sections.map((section) => (
          <div key={section.id} style={panelStyle}>
            <div className="d-flex justify-content-between align-items-start mb-2">
              <input
                type="text"
                value={section.heading}
                onChange={(e) => updateSectionHeading(section.id, e.target.value)}
                disabled={!canEdit}
                placeholder="SECTION HEADING"
                style={{ fontWeight: "bold", fontSize: "16px", flex: 1, marginRight: "12px" }}
                className="form-control"
              />
              <span className="badge bg-secondary me-2 mt-2">{section.type === "list" ? "List" : "Clauses"}</span>
              {canEdit && (
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeSection(section.id)}>
                  Remove Section
                </button>
              )}
            </div>

            {section.type === "list" ? (
              <>
                {section.items.map((item, idx) => (
                  <div key={idx} className="d-flex align-items-start gap-2 mb-2">
                    <span style={{ marginTop: "8px", minWidth: "24px" }}>{idx + 1}.</span>
                    <textarea
                      className="form-control"
                      value={item}
                      onChange={(e) => updateItem(section.id, idx, e.target.value)}
                      disabled={!canEdit}
                      rows={Math.max(1, Math.ceil((item || "").length / 100))}
                    />
                    {canEdit && (
                      <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeItem(section.id, idx)}>
                        ✕
                      </button>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addItem(section.id)}>
                    + Add Item
                  </button>
                )}
              </>
            ) : (
              <>
                <textarea
                  className="form-control mb-3"
                  value={section.intro}
                  onChange={(e) => updateSectionIntro(section.id, e.target.value)}
                  disabled={!canEdit}
                  placeholder="Intro paragraph (shown before clause 1, optional)"
                  rows={2}
                />
                {section.clauses.map((clause, cIdx) => (
                  <div key={cIdx} style={{ borderLeft: "3px solid #adb5bd", paddingLeft: "12px", marginBottom: "14px" }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={clause.number}
                        onChange={(e) => updateClauseField(section.id, cIdx, "number", e.target.value)}
                        disabled={!canEdit}
                        style={{ width: "50px" }}
                        className="form-control form-control-sm"
                      />
                      <input
                        type="text"
                        value={clause.heading}
                        onChange={(e) => updateClauseField(section.id, cIdx, "heading", e.target.value)}
                        disabled={!canEdit}
                        placeholder="CLAUSE HEADING"
                        className="form-control form-control-sm"
                        style={{ fontWeight: "bold" }}
                      />
                      {canEdit && (
                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeClause(section.id, cIdx)}>
                          Remove Clause
                        </button>
                      )}
                    </div>

                    {clause.paragraphs.map((para, pIdx) => (
                      <div key={pIdx} className="d-flex align-items-start gap-2 mb-2">
                        <textarea
                          className="form-control"
                          value={para}
                          onChange={(e) => updateParagraph(section.id, cIdx, pIdx, e.target.value)}
                          disabled={!canEdit}
                          rows={Math.max(1, Math.ceil((para || "").length / 100))}
                        />
                        {canEdit && (
                          <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeParagraph(section.id, cIdx, pIdx)}>
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                    {canEdit && (
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addParagraph(section.id, cIdx)}>
                        + Add Paragraph
                      </button>
                    )}
                  </div>
                ))}
                {canEdit && (
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => addClause(section.id)}>
                    + Add Clause
                  </button>
                )}
              </>
            )}
          </div>
        ))
      )}
    </>
  );
}

/* ================================ SHELL ================================ */

const TermsConditionsConfig = () => {
  const canView = hasPermission("TERMS_CONDITIONS_CONFIG", "view");
  const canEdit = hasPermission("TERMS_CONDITIONS_CONFIG", "edit");
  const initialTab = new URLSearchParams(window.location.search).get("tab") === "po" ? "po" : "quotation";
  const [activeTab, setActiveTab] = useState(initialTab); // 'quotation' | 'po'

  if (!canView) {
    return (
      <Layout>
        <div className="container-fluid p-4">
          <p>You don't have access to view this page. Please contact admin.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid p-4">
        <h2 className="m-0 mb-3">Terms & Conditions Configuration</h2>

        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === "quotation" ? "active" : ""}`}
              onClick={() => setActiveTab("quotation")}
            >
              Quotation
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === "po" ? "active" : ""}`}
              onClick={() => setActiveTab("po")}
            >
              Purchase Order
            </button>
          </li>
        </ul>

        {/* Both panels stay mounted (just hidden) so switching tabs never
            re-fetches or discards unsaved edits in the other template. */}
        <div style={{ display: activeTab === "quotation" ? "block" : "none" }}>
          <QuotationTermsPanel canEdit={canEdit} />
        </div>
        <div style={{ display: activeTab === "po" ? "block" : "none" }}>
          <PoTermsPanel canEdit={canEdit} />
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Layout>
  );
};

export default TermsConditionsConfig;